import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { MaskedLines } from "@/components/onboarding/MaskedText";
import Field from "@/components/onboarding/Field";
import { api, extractErrorMessage } from "@/lib/api";

const LOGO_URL =
  "https://sonarbanglahaat.com/wp-content/uploads/2025/12/cropped-Logo-Photoroom-1.png";

const EMPTY = {
  account_holder_name: "",
  bank_name: "",
  account_number: "",
  ifsc: "",
  upi_id: "",
};

export default function VendorPayout() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/vendor/payout/${token}`)
      .then((r) => {
        setCtx(r.data);
        if (r.data.already_submitted) setDone(true);
      })
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.account_holder_name) missing.push("Account holder name");
    if (!form.bank_name) missing.push("Bank name");
    if (!/^[0-9]{9,18}$/.test(form.account_number)) missing.push("Valid account number");
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc.toUpperCase()))
      missing.push("Valid IFSC (AAAA0XXXXXX)");
    if (missing.length) {
      toast.error(`Please fix: ${missing.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vendor/payout/${token}`, form);
      setDone(true);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] flex items-center justify-center">
        <div className="font-editorial italic text-[color:var(--sbh-muted)]">Loading…</div>
      </div>
    );
  }

  if (error || !ctx) {
    return (
      <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="eyebrow">Sonar Bangla Haat</div>
          <h1 className="mt-3 font-editorial text-[36px] leading-tight">
            This link isn&apos;t active anymore.
          </h1>
          <p className="mt-4 text-[color:var(--sbh-ink-2)]">
            {error || "Your payout link may have expired or already been used. If this seems wrong, please reply to your approval email or contact support."}
          </p>
          <Link
            to="/"
            className="btn-editorial mt-8 inline-flex"
            data-testid="payout-back-home"
          >
            Back to onboarding
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] flex items-center justify-center px-6">
        <div className="grain fixed inset-0 pointer-events-none z-[5]" />
        <div className="relative z-10 max-w-lg text-center">
          <motion.div
            className="mx-auto w-20 h-20 border border-[color:var(--sbh-terracotta)] flex items-center justify-center rounded-full"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Check className="w-8 h-8 text-[color:var(--sbh-terracotta)]" strokeWidth={2.4} />
          </motion.div>
          <div className="mt-6 eyebrow">Onboarded</div>
          <h1 className="mt-3 font-editorial text-[36px] md:text-[44px] leading-tight">
            You&apos;re in, {ctx.owner_name}.
          </h1>
          <p className="mt-4 text-[color:var(--sbh-ink-2)]">
            Your payout details for <b>{ctx.brand_name}</b> are securely on file. Your storefront goes live within 48–72 hours — our team will guide you through your first product listing.
          </p>
          <a
            href="https://sonarbanglahaat.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-editorial mt-8 inline-flex"
            data-testid="payout-visit-site"
          >
            Visit sonarbanglahaat.com
            <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] relative">
      <div className="grain fixed inset-0 pointer-events-none z-[5]" />
      <header className="relative z-10 px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" data-testid="payout-logo-link">
          <img
            src={LOGO_URL}
            alt="Sonar Bangla Haat"
            className="h-11 md:h-14 w-auto"
            draggable="false"
          />
        </Link>
        <div className="hidden md:flex items-center gap-2 text-[color:var(--sbh-ink-2)]">
          <ShieldCheck className="w-4 h-4 text-[color:var(--sbh-terracotta)]" strokeWidth={1.8} />
          <span className="eyebrow">Private · Secure Payout Link</span>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-24">
        <div className="eyebrow">Chapter 06 · Payout Details</div>
        <h1 className="mt-3 font-editorial text-[42px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.01em] max-w-3xl">
          <MaskedLines
            lines={[
              `Welcome to the Haat, ${ctx.owner_name || "friend"}.`,
              <span key="i" className="italic text-[color:var(--sbh-terracotta)]">Let&apos;s open your payout.</span>,
            ]}
          />
        </h1>
        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--sbh-ink-2)]">
          Your application for <b>{ctx.brand_name}</b> has been approved. Add your bank details below — they&apos;re encrypted at rest and used only for vendor payouts.
        </p>

        <form
          onSubmit={submit}
          className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2 max-w-3xl"
          data-testid="payout-form"
        >
          <div className="md:col-span-2">
            <Field
              label="Account Holder Name (as per bank)"
              required
              value={form.account_holder_name}
              onChange={set("account_holder_name")}
              testId="payout-account-holder"
            />
          </div>
          <Field
            label="Bank Name"
            required
            value={form.bank_name}
            onChange={set("bank_name")}
            testId="payout-bank-name"
          />
          <Field
            label="Account Number"
            required
            value={form.account_number}
            onChange={set("account_number")}
            testId="payout-account-number"
            maxLength={18}
          />
          <Field
            label="IFSC Code"
            required
            value={form.ifsc}
            onChange={(v) => set("ifsc")(v.toUpperCase())}
            testId="payout-ifsc"
            maxLength={11}
          />
          <Field
            label="UPI ID (optional)"
            value={form.upi_id}
            onChange={set("upi_id")}
            testId="payout-upi"
          />

          <div className="md:col-span-2 mt-10 flex items-center gap-6 flex-wrap">
            <button
              type="submit"
              className="btn-editorial"
              disabled={busy}
              data-testid="payout-submit-btn"
            >
              {busy ? "Submitting…" : "Submit Payout Details"}
              <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
            </button>
            <div className="flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)]">
              <Lock className="w-3.5 h-3.5" strokeWidth={2} />
              Private link · Never shared
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
